import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Lock, Shield, Users, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ExportPermissions = ({ reducedMotion }) => {
  const [roles, setRoles] = useState([
    { id: 1, name: 'Admin', can_export_calls: true, can_export_transcripts: true, can_export_reports: true },
    { id: 2, name: 'Manager', can_export_calls: true, can_export_transcripts: false, can_export_reports: true },
    { id: 3, name: 'Analyst', can_export_calls: false, can_export_transcripts: false, can_export_reports: true },
    { id: 4, name: 'Viewer', can_export_calls: false, can_export_transcripts: false, can_export_reports: false }
  ]);

  const handleTogglePermission = (roleId, permission) => {
    setRoles(roles.map(role => {
      if (role.id === roleId) {
        return { ...role, [permission]: !role[permission] };
      }
      return role;
    }));
    toast.success('Permissions updated');
  };

  const getRoleBadge = (roleName) => {
    if (roleName === 'Admin') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (roleName === 'Manager') return 'bg-ink-100 dark:bg-ink-8000/20 text-ink-500 border-ink-200 dark:border-ink-800';
    if (roleName === 'Analyst') return 'bg-ink-500 text-ink-500 border-ink-300 dark:border-ink-700';
    return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="export-permissions-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Lock className="w-5 h-5 text-red-400" />
          Export Permissions
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Role-based access control for data exports
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {roles.map((role, index) => (
            <div
              key={role.id}
              className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50"
              style={{
                animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
              }}
              data-testid={`role-permissions-${role.id}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-red-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-100" data-testid="role-name">{role.name}</h4>
                  <Badge className={getRoleBadge(role.name)} data-testid="role-badge">
                    {role.name} Role
                  </Badge>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300">Export Calls</span>
                  </div>
                  <button
                    onClick={() => handleTogglePermission(role.id, 'can_export_calls')}
                    className="flex items-center gap-2"
                    data-testid={`toggle-calls-${role.id}`}
                  >
                    {role.can_export_calls ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300">Export Transcripts</span>
                  </div>
                  <button
                    onClick={() => handleTogglePermission(role.id, 'can_export_transcripts')}
                    className="flex items-center gap-2"
                    data-testid={`toggle-transcripts-${role.id}`}
                  >
                    {role.can_export_transcripts ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300">Export Reports</span>
                  </div>
                  <button
                    onClick={() => handleTogglePermission(role.id, 'can_export_reports')}
                    className="flex items-center gap-2"
                    data-testid={`toggle-reports-${role.id}`}
                  >
                    {role.can_export_reports ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
          <p className="text-xs text-slate-400">
            <strong className="text-amber-400">Security Note:</strong> Export permissions are enforced at the API level. Changes take effect immediately for all users with the modified role.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExportPermissions;